import { useCallback } from "react";

import {
  ENTRY_DATA_SECTION_KEYS as SECTION_KEYS,
  ENTRY_DATA_INVESTORS_GROUP_TABLE_KEY as INVESTORS,
} from "@frontend/common";

import {
  Investors,
  VentureCapitalInvestorsShellKeys,
} from "@/types/ventureCapital.types";
import {
  useTableCreatorFocusStatus,
  DEFAULT_FOCUS_STATUS,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import type { PositionInfoProps } from "../VentureCapitalInvestorsCreatorShell";
import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorShellReturn {
  handleKeyDownToClearInvestorsShell: (event: KeyboardEvent) => void;
  handleClickToClearInvestorsShell: () => void;
}

export const useHandleVentureCapitalInvestorsCreatorShell = (
  positionPropsInfo: PositionInfoProps,
): UseHandleTableCreatorShellReturn => {
  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const { info, changeInfo } = useTableCreatorPositionInfo();
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());

  const handleClearShell = useCallback(() => {
    const clearInvestorsShell = (): ArticleEntryData => {
      return {
        ...entryData,
        [SECTION_KEYS.ventureCapital]: {
          ...entryData[SECTION_KEYS.ventureCapital],
          [INVESTORS]: {
            ...entryData[SECTION_KEYS.ventureCapital]?.[INVESTORS],
            [info.shellKey as VentureCapitalInvestorsShellKeys]: {
              ...entryData[SECTION_KEYS.ventureCapital]?.[INVESTORS]?.[
                info.shellKey as VentureCapitalInvestorsShellKeys
              ],
              value: [],
            },
          } as Investors,
        },
      };
    };

    if (info.groupTableKey === INVESTORS) {
      setEntryData(clearInvestorsShell());
      changeCurrentHistory({
        entryData: clearInvestorsShell(),
        changedDataInfo: {
          target: "shell",
          action: "clear",
          position: {
            ...positionPropsInfo,
            value: null,
            valueIndex: null,
          },
        },
      });
    }

    changeFocusStatus(DEFAULT_FOCUS_STATUS);
    changeInfo(DEFAULT_POSITION_INFO);
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    entryData,
    info,
    positionPropsInfo,
    setEntryData,
  ]);

  const handleClickToClearInvestorsShell = () => {
    if (focusStatus.type === "clear") {
      handleClearShell();
    }
  };

  const handleKeyDownToClearInvestorsShell = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearShell();
      }
    },
    [focusStatus.type, handleClearShell],
  );

  return {
    handleClickToClearInvestorsShell,
    handleKeyDownToClearInvestorsShell,
  };
};
